import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import SEO from '../SEO/SEO';
import { motion } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const BranchLocator = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [banner, setBanner] = useState(null);
  const [loadingBanner, setLoadingBanner] = useState(true);
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const CARDS_PER_PAGE = 6;
  const [noticeContent, setNoticeContent] = useState('');

  useEffect(() => {
    const fetchNotice = async () => {
      try {
        const response = await axios.get('/api/admin/branch-locator/notice');
        if (response.data.success && response.data.data) {
          setNoticeContent(response.data.data.content);
        }
      } catch (error) {
        console.error('Error fetching notice:', error);
      }
    };
    fetchNotice();
  }, []);

  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const response = await axios.get('/api/banners/active?page_type=branch_locator');
        if (response.data.success && response.data.data.length > 0) {
          setBanner(response.data.data[0]);
        }
      } catch (error) {
        console.error('Error fetching banner:', error);
      } finally {
        setLoadingBanner(false);
      }
    };

    fetchBanner();
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingBranches(true);
        const response = await axios.get('/api/admin/branches/active');
        if (response.data.success) {
          // Map backend snake_case to frontend camelCase
          const mappedBranches = response.data.data.map(b => ({
            ...b,
            mapLink: b.map_link,
            reviewLink: b.review_link,
            cardLink: b.card_link
          }));
          setBranches(mappedBranches);
        }
      } catch (error) {
        console.error('Error fetching branches:', error);
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, []);

  // Use the fetched branches
  const allBranches = branches;

  // Get unique states for filter from dynamic branches
  const uniqueStates = useMemo(() => {
    const states = [...new Set(branches.map(branch => branch.state))];
    return states.sort();
  }, [branches]);

  // Filter branches based on search query and selected state
  const filteredBranches = useMemo(() => {
    let filtered = allBranches;

    if (selectedState) {
      filtered = filtered.filter(branch => branch.state === selectedState);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(branch =>
        branch.name.toLowerCase().includes(query) ||
        branch.city.toLowerCase().includes(query) ||
        branch.pincode.toLowerCase().includes(query) ||
        branch.address.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [searchQuery, selectedState, allBranches]);

  // Pagination logic
  const totalPages = Math.ceil(filteredBranches.length / CARDS_PER_PAGE);
  const startIndex = (currentPage - 1) * CARDS_PER_PAGE;
  const endIndex = startIndex + CARDS_PER_PAGE;
  const currentBranches = filteredBranches.slice(startIndex, endIndex);

  // Reset to page 1 when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedState]);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };
  
  
  
  // Schema for SEO
  const branchLocatorSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://ahamhfc.com/#organization",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "legalName": "AHAM HOUSING FINANCE LIMITED",
        "alternateName": "Aham Housing Finance Private Limited",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED is an affordable housing finance company helping customers access home loans across multiple regions in India.",
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "address": {
          "@type": "PostalAddress",
         "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600034",
          "addressCountry": "IN"
        },
        "sameAs": [
          "https://www.facebook.com/ahamhfc",
          "https://www.instagram.com/aham_housingfinance/",
          "https://linkedin.com/company/ahamhfc",
          "https://www.youtube.com/@ahamhfc"
        ]
      },
      {
        "@type": "FinancialService",
        "@id": "https://ahamhfc.com/#financialservice",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "url": "https://ahamhfc.com/",
        "logo": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png",
        "description": "AHAM HOUSING FINANCE LIMITED offers home loans and affordable housing finance solutions across multiple customer segments in India.",
        "areaServed": {
          "@type": "Country",
          "name": "India"
        },
        "telephone": "+91 78239 11555",
        "email": "info@ahamhfc.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "New No. 2 & 4, Old No. 24 & 25, 3rd Floor, Sivaganga Road, Nungambakkam",
          "addressLocality": "Chennai",
          "addressRegion": "Tamil Nadu",
          "postalCode": "600034",
          "addressCountry": "IN"
        }
      },
      {
        "@type": "WebSite",
        "@id": "https://ahamhfc.com/#website",
        "url": "https://ahamhfc.com/",
        "name": "AHAM HOUSING FINANCE LIMITED",
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "inLanguage": "en-IN"
      },
      {
        "@type": "WebPage",
        "@id": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan/#webpage",
        "url": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan",
        "name": "Branch Locator - Aham Housing Finance",
        "description": "Find Aham Housing Finance branches and service locations for home loan support and customer assistance.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "publisher": {
          "@id": "https://ahamhfc.com/#organization"
        },
        "primaryImageOfPage": {
          "@type": "ImageObject",
          "contentUrl": "https://ahamhfc.com/static/media/Aham_logo_1.484f76cda17dcb7317e5.png"
        }
      },
      {
        "@type": "CollectionPage",
        "@id": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan/#collectionpage",
        "url": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan",
        "name": "Aham Housing Finance Branch Locator",
        "description": "Browse Aham Housing Finance branch and service-area locations across multiple cities and regions.",
        "isPartOf": {
          "@id": "https://ahamhfc.com/#website"
        },
        "about": {
          "@id": "https://ahamhfc.com/#financialservice"
        },
        "mainEntity": {
          "@id": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan/#itemlist"
        }
      },
      {
        "@type": "ItemList",
        "@id": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan/#itemlist",
        "name": "Aham Housing Finance Branch Locations",
        "numberOfItems": 33,
        "itemListOrder": "https://schema.org/ItemListUnordered",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Avadi" },
          { "@type": "ListItem", "position": 2, "name": "Chengalpet" },
          { "@type": "ListItem", "position": 3, "name": "Kanchipuram" },
          { "@type": "ListItem", "position": 4, "name": "Kundrathur" },
          { "@type": "ListItem", "position": 5, "name": "Tiruvallur" },
          { "@type": "ListItem", "position": 6, "name": "Vellore" },
          { "@type": "ListItem", "position": 7, "name": "Viluppuram" },
          { "@type": "ListItem", "position": 8, "name": "Coimbatore" },
          { "@type": "ListItem", "position": 9, "name": "Dindigul" },
          { "@type": "ListItem", "position": 10, "name": "Erode" },
          { "@type": "ListItem", "position": 11, "name": "Madurai" },
          { "@type": "ListItem", "position": 12, "name": "Salem" },
          { "@type": "ListItem", "position": 13, "name": "Trichy" },
          { "@type": "ListItem", "position": 14, "name": "Usilampatti" },
          { "@type": "ListItem", "position": 15, "name": "Theni" },
          { "@type": "ListItem", "position": 16, "name": "Attur" },
          { "@type": "ListItem", "position": 17, "name": "Tanjore" },
          { "@type": "ListItem", "position": 18, "name": "Pudukottai" },
          { "@type": "ListItem", "position": 19, "name": "T.Dasarahalli, Bangalore" },
          { "@type": "ListItem", "position": 20, "name": "Chikkaballapur" },
          { "@type": "ListItem", "position": 21, "name": "Hoskote" },
          { "@type": "ListItem", "position": 22, "name": "Mysuru" },
          { "@type": "ListItem", "position": 23, "name": "Tumakuru/Tumkur" },
          { "@type": "ListItem", "position": 24, "name": "Kolar" },
          { "@type": "ListItem", "position": 25, "name": "Anakapally" },
          { "@type": "ListItem", "position": 26, "name": "Guntur" },
          { "@type": "ListItem", "position": 27, "name": "Nellore" },
          { "@type": "ListItem", "position": 28, "name": "Rajahmundry" },
          { "@type": "ListItem", "position": 29, "name": "Vijayawada" },
          { "@type": "ListItem", "position": 30, "name": "Vizag/Visakhapatnam" },
          { "@type": "ListItem", "position": 31, "name": "LB Nagar, Hyderabad" },
          { "@type": "ListItem", "position": 32, "name": "Warangal" },
          { "@type": "ListItem", "position": 33, "name": "Siddipet" }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://ahamhfc.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Branch Locator",
            "item": "https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan"
          }
        ]
      }
    ]
  };
  

  return (
    <div className="bg-gray-50 min-h-screen">

      <SEO
        title="Aham Housing Finance Branch Locator Near You"
        description="Find your nearest Aham Housing Finance branch for home loans without income proof. Get doorstep support, easy documentation and quick approvals today."
        keywords="Aham Housing Finance branch locator, Aham branch locator, Aham Housing Finance branches, Aham branch locations, housing finance branch locator, home loan branches near me, Aham Housing Finance near me, Aham office locations, Aham branch address, Aham contact branch, housing loan branch network, Aham customer service branches, branch locator home loans, Aham Tamil Nadu branches, Aham Andhra Pradesh branches, Aham Karnataka branches, nearest Aham branch, Aham Housing Finance contact details, branch network Aham Housing Finance, home loan branch finder"
        canonical="https://ahamhfc.com/branch-locator-aham-housing-finance-home-loan"
        ogTitle="Aham Housing Finance Branch Locator Near You"
        ogDescription="Find your nearest Aham Housing Finance branch for home loans without income proof. Get doorstep support, easy documentation and quick approvals today."
        schema={branchLocatorSchema}
    />

      <section className="relative min-h-[60vh] flex items-center justify-start overflow-hidden">
        {loadingBanner ? (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-pulse">
                <div className="h-8 w-48 bg-white/20 rounded mx-auto mb-4"></div>
                <div className="h-4 w-64 bg-white/10 rounded mx-auto"></div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${banner?.image_url ? (banner.image_url.startsWith('http') ? banner.image_url : `${banner.image_url}`) : 'img/ahamlife/Locator.png'}?t=${banner?.updated_at ? new Date(banner.updated_at).getTime() : Date.now()})`,
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
            </div>

            <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="max-w-4xl"
              >
                {/* Breadcrumb Navigation */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.6 }}
                  className="flex items-center space-x-2 mb-6"
                >
                  <Link
                    to="/"
                    className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
                  >
                    Home
                  </Link>
                  <ChevronRightIcon className="h-4 w-4 text-white/60" />
                  <span className="text-yellow-300 text-sm font-semibold">Branch Locator</span>
                </motion.div>

                <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white mb-6 leading-tight">
                  {banner?.title || 'Branch Locator'}
                </h1>
                <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed font-medium">
                  {banner?.subtitle || 'Get accurate directions, service details, and support across our growing network of locations.'}
                </p>
              </motion.div>
            </div>
          </>
        )}
      </section>

      {/* Search & Cards Section */}
      <section className="container mx-auto px-4 py-12">
        {/* Search Bar & Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mb-12"
        >
          <div className="flex flex-col md:flex-row gap-4">
            {/* State Filter */}
            <div className="md:w-1/3 relative">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                className="w-full pl-4 pr-10 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-200 focus:border-primary-500 transition-all duration-200 text-lg appearance-none bg-white cursor-pointer"
              >
                <option value="">All States</option>
                {uniqueStates.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronRightIcon className="h-5 w-5 text-gray-400 rotate-90" />
              </div>
            </div>

            {/* Search Input */}
            <div className="md:w-2/3 relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by city, pincode, or address..."
                data-testid="branch-search-input"
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-200 focus:border-primary-500 transition-all duration-200 text-lg"
              />
            </div>
          </div>

          {
            (searchQuery || selectedState) && (
              <div className="mt-3 flex items-center justify-between">
                <p className="text-gray-600 text-sm">
                  Found <span className="font-semibold text-primary-600">{filteredBranches.length}</span> branch{filteredBranches.length !== 1 ? 'es' : ''}
                  {selectedState && <span> in <span className="font-semibold text-primary-600">{selectedState}</span></span>}
                  {searchQuery && <span> matching "{searchQuery}"</span>}
                </p>
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedState('');
                  }}
                  className="text-sm text-red-500 hover:text-red-700 font-medium hover:underline"
                >
                  Clear Filters
                </button>
              </div>
            )
          }
        </motion.div>

        {/* Branch Cards Grid */}
        {
          loadingBranches ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : currentBranches.length > 0 ? (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12"
              >
                {currentBranches.map((branch, index) => (
                  <motion.div
                    key={branch.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
                    data-testid={`branch-card-${branch.id}`}
                  >
                    {/* Card Header */}
                    <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-white mb-1">
                            {branch.name}
                          </h3>
                          <p className="text-white/90 text-sm">{branch.city}, {branch.state}</p>
                        </div>
                        <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                          <MapPinIcon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Card Body */}
                    <div className="px-6 py-5 space-y-4">
                      {/* Address */}
                      <div className="flex items-start space-x-3">
                        <MapPinIcon className="h-5 w-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-700 leading-relaxed">{branch.address}</p>
                          <p className="text-xs text-gray-500 mt-1">Pincode: {branch.pincode}</p>
                        </div>
                      </div>

                      {/* Contact */}
                      <div className="flex items-center space-x-3">
                        <PhoneIcon className="h-5 w-5 text-primary-600 flex-shrink-0" />
                        <a
                          href={`tel:${branch.contact}`}
                          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {branch.contact}
                        </a>
                      </div>

                      {/* CTA Buttons */}
                      <div className="flex gap-3 pt-2">
                        <a
                          href={branch.mapLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-3 px-4 rounded-lg transition-all duration-300 text-center"
                        >
                          Direction
                        </a>
                        <a
                          href={branch.reviewLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 bg-white hover:bg-gray-50 text-primary-600 border-2 border-primary-600 hover:border-primary-700 text-sm font-semibold py-3 px-4 rounded-lg transition-all duration-300 text-center"
                        >
                          Google Reviews
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>

              {/* Pagination */}
              {
                totalPages > 1 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="flex justify-center items-center space-x-4"
                  >
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${currentPage === 1
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 text-white transform hover:scale-105 hover:shadow-md'
                        }`}
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                      <span>Previous</span>
                    </button>

                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">
                        Page <span className="text-primary-600 font-bold">{currentPage}</span> of <span className="font-bold">{totalPages}</span>
                      </span>
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${currentPage === totalPages
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-700 text-white transform hover:scale-105 hover:shadow-md'
                        }`}
                    >
                      <span>Next</span>
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </motion.div>
                )
              }
            </>
          ) : (
            /* No Results */
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center py-16"
            >
              <MapPinIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No branches found</h3>
              <p className="text-gray-500">Try adjusting your search query</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-6 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
              >
                Clear Search
              </button>
            </motion.div>
          )
        }
      </section>

      {/* Branch Timings Info Section */}
      <section className="bg-primary-600 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <p className="text-lg" dangerouslySetInnerHTML={{
            __html: noticeContent || '<span class="font-semibold">Branch Timings:</span> 9.30 AM to 6 PM (Holidays: All Sundays, Public/National holidays, and 2nd Saturday)'
          }} />
        </div>
      </section>
    </div>
  );
};

export default BranchLocator;
